/**
 * Standings, computed from games rather than stored, so a corrected
 * scoresheet immediately corrects the table.
 */

import type { Game, StandingRow, Team, UUID } from './types';

export interface StandingsRules {
  pointsForWin: number;
  pointsForTie: number;
  pointsForLoss: number;
  /** Order of tiebreakers applied after league points. */
  tiebreakers: Array<'plusMinus' | 'pointsFor' | 'won' | 'endsWon' | 'headToHead'>;
}

export const DEFAULT_RULES: StandingsRules = {
  pointsForWin: 2,
  pointsForTie: 1,
  pointsForLoss: 0,
  tiebreakers: ['headToHead', 'plusMinus', 'pointsFor', 'endsWon'],
};

interface Accumulator {
  teamId: UUID;
  teamName: string;
  scheduleNo: number;
  played: number;
  won: number;
  lost: number;
  tied: number;
  points: number;
  pointsFor: number;
  pointsAgainst: number;
  endsWon: number;
  blankEnds: number;
}

/**
 * `games` should be every game in the schedule; anything not `final` or
 * `forfeit` is ignored so an in-progress draw does not disturb the table.
 */
export function computeStandings(
  teams: readonly Team[],
  games: readonly Game[],
  rules: StandingsRules = DEFAULT_RULES,
): StandingRow[] {
  const acc = new Map<UUID, Accumulator>();
  for (const team of teams) {
    acc.set(team.id, {
      teamId: team.id,
      teamName: team.name,
      scheduleNo: team.scheduleNo,
      played: 0, won: 0, lost: 0, tied: 0,
      points: 0, pointsFor: 0, pointsAgainst: 0,
      endsWon: 0, blankEnds: 0,
    });
  }

  const headToHead = new Map<string, number>();
  const h2hKey = (a: UUID, b: UUID) => `${a}|${b}`;

  for (const game of games) {
    if (game.status !== 'final' && game.status !== 'forfeit') continue;
    if (!game.homeTeamId || !game.awayTeamId) continue;

    const home = acc.get(game.homeTeamId);
    const away = acc.get(game.awayTeamId);
    if (!home || !away) continue;

    const hs = game.homeScore ?? 0;
    const as = game.awayScore ?? 0;

    home.played++; away.played++;
    home.pointsFor += hs; home.pointsAgainst += as;
    away.pointsFor += as; away.pointsAgainst += hs;

    for (const end of game.ends) {
      if (end.homePoints > 0) home.endsWon++;
      else if (end.awayPoints > 0) away.endsWon++;
      else { home.blankEnds++; away.blankEnds++; }
    }

    if (hs > as) {
      home.won++; away.lost++;
      home.points += rules.pointsForWin; away.points += rules.pointsForLoss;
      headToHead.set(h2hKey(home.teamId, away.teamId), (headToHead.get(h2hKey(home.teamId, away.teamId)) ?? 0) + 1);
    } else if (as > hs) {
      away.won++; home.lost++;
      away.points += rules.pointsForWin; home.points += rules.pointsForLoss;
      headToHead.set(h2hKey(away.teamId, home.teamId), (headToHead.get(h2hKey(away.teamId, home.teamId)) ?? 0) + 1);
    } else {
      home.tied++; away.tied++;
      home.points += rules.pointsForTie; away.points += rules.pointsForTie;
    }
  }

  const rows = [...acc.values()].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;

    for (const tb of rules.tiebreakers) {
      if (tb === 'headToHead') {
        const aOverB = headToHead.get(h2hKey(a.teamId, b.teamId)) ?? 0;
        const bOverA = headToHead.get(h2hKey(b.teamId, a.teamId)) ?? 0;
        if (aOverB !== bOverA) return bOverA - aOverB;
        continue;
      }
      const av = tb === 'plusMinus' ? a.pointsFor - a.pointsAgainst
        : tb === 'pointsFor' ? a.pointsFor
        : tb === 'won' ? a.won
        : a.endsWon;
      const bv = tb === 'plusMinus' ? b.pointsFor - b.pointsAgainst
        : tb === 'pointsFor' ? b.pointsFor
        : tb === 'won' ? b.won
        : b.endsWon;
      if (av !== bv) return bv - av;
    }
    return a.teamName.localeCompare(b.teamName);
  });

  return rows.map((r, i) => ({
    ...r,
    plusMinus: r.pointsFor - r.pointsAgainst,
    rank: i + 1,
  }));
}

/** Totals a line score into a final result, the way a scoreboard does. */
export function scoreFromEnds(ends: ReadonlyArray<{ homePoints: number; awayPoints: number }>) {
  return ends.reduce(
    (t, e) => ({ home: t.home + e.homePoints, away: t.away + e.awayPoints }),
    { home: 0, away: 0 },
  );
}

/**
 * An end may only be scored by one side, and never by more than eight.
 * Returns the problems rather than throwing so a form can show them inline.
 */
export function validateEnds(
  ends: ReadonlyArray<{ endNumber: number; homePoints: number; awayPoints: number }>,
): string[] {
  const problems: string[] = [];
  const seen = new Set<number>();

  for (const end of ends) {
    const label = `End ${end.endNumber}`;
    if (seen.has(end.endNumber)) problems.push(`${label} is listed twice.`);
    seen.add(end.endNumber);

    if (end.homePoints < 0 || end.awayPoints < 0) {
      problems.push(`${label} cannot have a negative score.`);
    }
    if (end.homePoints > 8 || end.awayPoints > 8) {
      problems.push(`${label} cannot be more than eight — that is every stone in the house.`);
    }
    if (end.homePoints > 0 && end.awayPoints > 0) {
      problems.push(`${label} cannot be scored by both teams.`);
    }
  }
  return problems;
}
